import React, { useState, useEffect, useRef } from 'react';
import { PaperClipIcon } from '@heroicons/react/20/solid';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import api from '../utils/api';
import { useClient } from '../contexts/ClientContext';
import { useAssignedTo } from '../contexts/AssignedToContext';
import Select from 'react-select';
import { CloudArrowUpIcon } from "@heroicons/react/24/outline"; // or 'solid'

const minimalToolbar = [
  ['bold', 'italic', 'underline'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link'],
];

export default function TicketForm({
  onSuccess,
  isAdmin = false,
  priorityOptions = [],
  statusOptions = [],
  userInfo = {},
  // selectedClientId = '',
  selectedTicketId = '',
  selectedStatusId = '',
  selectedPriorityId = '',
  selectedTicketChildId = '',
  module_name = '',
  selectedIssueType = '',
  ticket = [],
  onClose = () => { },
  onNext,
  onPrevious,
  hasNext,
  hasPrevious,
}) {
  const [form, setForm] = useState({
    client: '',
    customer: '',
    module: module_name || '',
    title: '',
    priority: '',
    status: '',
    description: '',
    attachments: [],
    assigned_to: [], // Changed to array to support multi-select
    issue_type: '',
    enhancement_type: '',
    enhancement_hours: '',
    internal_cc: [], // Array for CC'd users
    recovery_steps: '', // Problem Recovery field (developer + Bug + Testing)
    restart_steps: '',  // Restart Procedure field (developer + Bug + Testing)
  });
  const { selectedClientId, setSelectedClientId } = useClient();
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [moduleOptions, setModuleOptions] = useState([]);
  const { clients: clientList } = useClient();
  const [customerOptions, setCustomerOptions] = useState([]);
  const [isCustomerLoading, setIsCustomerLoading] = useState(false);
  const [isModuleLoading, setIsModuleLoading] = useState(false);
  const isEditMode = !!selectedTicketId;
  const { assignedToList } = useAssignedTo();
  const [showAssignee, setShowAssignee] = useState(true);
  const [isDragging, setIsDragging] = useState(false);


  const toTitleCase = (str) =>
    str
      ?.toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  const assigneeOptions = assignedToList?.data?.map((user) => ({
    value: user.user_id,
    label: `${toTitleCase(user.first_name)} ${toTitleCase(user.last_name)} (${toTitleCase(user.designation_name)})`
  })) || [];

  const assigneeRef = useRef(null);
  const descriptionRef = useRef(null);

  useEffect(() => {
    if (isEditMode && ticket) {
      setForm((prev) => ({
        ...prev,
        client: ticket.client_key || ticket.client_name || '',
        customer: ticket.customer_name || '',
        module: ticket.module_name || ticket.module_id || ticket.module || module_name || '',
        internal_cc: typeof ticket.internal_cc === 'string' ? ticket.internal_cc.split(',').filter(Boolean) : (Array.isArray(ticket.internal_cc) ? ticket.internal_cc : []),
      }));
    }
  }, [isEditMode, ticket, module_name]);

  useEffect(() => {
    if (isEditMode && assigneeRef.current) {
      assigneeRef.current.focus(); //  focuses react-select input
    }
  }, [isEditMode]);

  useEffect(() => {
    if (isEditMode && descriptionRef.current) {
      descriptionRef.current.focus(); //  focuses react-select input
    }
  }, [isEditMode]);

  const [verticalHeadsOptions, setVerticalHeadsOptions] = useState([]);

  useEffect(() => {
    // Only fetch for internal tickets or admin/CA (NOT for clients)
    const fetchVerticalHeads = async () => {
      if (userInfo?.role_id && String(userInfo.role_id) === '2') {
        return;
      }
      try {
        const res = await api.get('/ticket/get-vertical-heads');
        if (res.data?.status) {
          setVerticalHeadsOptions(res.data.data.map(u => ({
            value: String(u.user_id),
            label: `${u.first_name} ${u.last_name} (${u.designation_name})`
          })));
        }
      } catch (err) {
        console.error('Failed to fetch Vertical Heads', err);
      }
    };
    fetchVerticalHeads();
  }, [userInfo]);

  useEffect(() => {
    if (descriptionRef.current) {
      const quill = descriptionRef.current.getEditor();
      quill.keyboard.addBinding({ key: 9 }, function () {
        // Prevent Quill tab behavior
        return true;
      });
    }
  }, []);

  // const handleKeyDown = (e) => {
  //   if (e.key === "Tab") {
  //     e.preventDefault();

  //     // find parent form
  //     const formEl = e.currentTarget.closest("form");
  //     if (!formEl) return;

  //     // list focusable elements inside the form
  //     const formElements = Array.from(
  //       formEl.querySelectorAll(
  //         "input, textarea, select, button, [contenteditable='true']"
  //       )
  //     ).filter(el => !el.disabled && el.tabIndex !== -1);

  //     // find currently focused element
  //     const index = formElements.indexOf(document.activeElement);

  //     if (e.shiftKey) {
  //       // Shift+Tab → go backwards
  //       const prev = formElements[index - 1];
  //       if (prev) prev.focus();
  //     } else {
  //       // Tab → go forwards
  //       const next = formElements[index + 1];
  //       if (next) next.focus();
  //     }
  //   }
  // };


  useEffect(() => {
    const fetchModules = async () => {
      try {
        const res = await api.get('/ticket/get-modules', {
          params: {
            child_id: selectedTicketId === '' ? userInfo?.child_id : selectedTicketChildId
          }
        });

        if (res.data.status && Array.isArray(res.data.data)) {
          const modules = res.data.data.map((item) => ({
            value: item.id,
            label: item.module_name
          }));
          setModuleOptions(modules);

          if (modules.length === 1) {
            setForm((prev) => ({ ...prev, module: modules[0].value }));
          } else {
            setForm((prev) => ({ ...prev, module: '' }));
          }
        }
      } catch (err) {
        console.error('Failed to fetch modules:', err);
      }
    };

    fetchModules();
    // if (userInfo?.role_id === '2' && userInfo?.cust_key) {
    //   fetchModules();
    // }
  }, [userInfo?.child_id]);


  useEffect(() => {
    if (
      selectedTicketChildId !== '' &&
      module_name &&
      moduleOptions.length > 0
    ) {
      const selectedModule = moduleOptions.find(
        (opt) => opt.label === module_name
      );
      if (selectedModule && form.module !== selectedModule.value) {
        setForm((prev) => ({
          ...prev,
          module: selectedModule.value
        }));
      }
    }
  }, [selectedTicketChildId, module_name, moduleOptions]);

  useEffect(() => {
    const roleId = Number(userInfo?.role_id);
    const initialStatus = selectedTicketId ? String(selectedStatusId) : '1';

    // For customers (Role 2), if the ticket is in a state that requires their explicit action (3, 12)
    // and those states are NOT in their allowed dropdown options, we MUST reset the dropdown to ""
    // so they are forced to pick an allowed status (like IN process or Closed).
    const isCustomerActionRequired = [3, 12].includes(Number(initialStatus));
    const finalInitialStatus = (isEditMode && roleId === 2 && isCustomerActionRequired) ? "" : initialStatus;

    const defaultForm = {
      client: '',
      customer: '',
      module: '',
      title: '',
      priority: selectedTicketId ? selectedPriorityId : '3',
      status: finalInitialStatus,
      description: '',
      attachments: [],
      assigned_to: [], // Initialize as empty array
      issue_type: selectedIssueType ? selectedIssueType : '',
      enhancement_type: ticket?.enhancement_type ?? '',
      enhancement_hours: ticket?.enhancement_hours ?? '',
      recovery_steps: '', // Reset on ticket change
      restart_steps: '',  // Reset on ticket change
    };
    setForm(defaultForm);
  }, [isAdmin, selectedTicketId, selectedPriorityId, selectedStatusId, selectedIssueType, userInfo]);

  useEffect(() => {
    setCustomerOptions([]); // clear customers when switching admin mode
  }, [isAdmin]);

  // Consolidate showAssignee logic to handle both initial load and status changes
  useEffect(() => {
    if (form.status == 3 || form.status == 5 || form.status == 12) {
      setShowAssignee(false);
    } else {
      setShowAssignee(true);
    }
  }, [form.status]);


  if (!assignedToList) {
    return <div>Loading assignees...</div>; // Optional loading state
  }

  useEffect(() => {
    if (ticket?.assign && assignedToList?.data?.length) {
      let defaultAssignee = String(ticket.assign);

      // Auto-populate Developer for CA if ticket is IN process and assigned to CA
      const roleId = Number(userInfo?.role_id);
      if ([1, 3].includes(roleId) && Number(ticket?.status_id) === 4 && ticket?.original_developer_id) {
        // If the customer sent it as IN process to the CA, the CA needs to send it to the developer.
        // By defaulting the dropdown to the developer, CA just clicks Submit.
        if (defaultAssignee === String(userInfo?.user_id)) {
          defaultAssignee = String(ticket.original_developer_id);
        }
      }

      setForm((prev) => ({
        ...prev,
        assigned_to: [defaultAssignee] // Wrap in array for consistency
      }));
    }
  }, [ticket, assignedToList, userInfo]);


  const handleChange = async (e) => {
    const { name, value } = e.target;

    // Always update the changed field first
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'client' && { customer: '', module: '' }),
      ...(name === 'customer' && { module: '' }),
      ...(name === 'issue_type' && { enhancement_type: '', enhancement_hours: '' }),
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: '',
    }));

    if (name === 'status') {
      // setShowAssignee logic is now in a centralized useEffect

      // Auto-assignment logic for transparency (User can override)
      let newAssignee = null;
      if (Number(value) === 8) {
        // If it was already tested, return it to that same tester for re-verification
        // Otherwise default to Support Head (Mari Sir - 101) for initial assignment
        newAssignee = ticket?.last_tester_id ? String(ticket.last_tester_id) : String(101);
      } else if ([9, 10, 11].includes(Number(value)) && ticket?.original_developer_id) {
        newAssignee = String(ticket.original_developer_id);
      } else if (Number(value) === 12) {
        const currentRoleId = Number(userInfo?.role_id);
        if ([1, 3].includes(currentRoleId)) {
          // CA selects Status 12 → goes directly to customer (backend handles it, hide assignee)
          newAssignee = null;
        } else {
          // Developer selects Status 12 → auto-assign to the Client Advocate
          newAssignee = ticket?.client_advocate_id ? String(ticket.client_advocate_id) : null;
        }
      } else if (Number(value) === 4 && Number(ticket?.status_id) === 9) {
        newAssignee = ticket?.client_advocate_id ? String(ticket.client_advocate_id) : null;
      } else if (Number(value) === 4 && [12].includes(Number(ticket?.status_id))) {
        // CA sends back to developer after receiving Status 12 from client/developer
        newAssignee = ticket?.original_developer_id ? String(ticket.original_developer_id) : null;
      } else {
        // For general status changes (like 'In Process' by dev), default back to the ticket's current assignee
        newAssignee = ticket?.assign ? String(ticket.assign) : null;
      }

      if (newAssignee) {
        setForm(prev => ({
          ...prev,
          assigned_to: [newAssignee]
        }));
      }
    }

    // console.log('showAssignee', showAssignee);

    // Fetch customers if client is selected
    if (name === 'client') {
      setModuleOptions([]);
      setCustomerOptions([]);
      setIsCustomerLoading(true);

      try {
        const res = await api.get('/ticket/get-customers', {
          params: { cust_key: value },
        });
        if (res.data.status) {
          setCustomerOptions(res.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch customers:', error);
      } finally {
        setIsCustomerLoading(false);
      }
    }

    // Fetch modules if customer is selected (only for admin)
    if (name === 'customer' && userInfo.role_id !== '2') {
      setIsModuleLoading(true);
      const selectedCustomer = customerOptions.find(c => c.cust_key === value);

      if (selectedCustomer?.modules) {
        const modules = selectedCustomer.modules.map(mod => ({
          value: mod.id,
          label: mod.module_name,
        }));
        setModuleOptions(modules);
      } else {
        setModuleOptions([]);
      }

      setIsModuleLoading(false);
    }
  };


  // const handleFileChange = (e) => {
  //   const newFiles = Array.from(e.target.files);

  //   // Avoid duplicates by name (optional)
  //   const existingNames = form.attachments.map((file) => file.name);
  //   const uniqueFiles = newFiles.filter((file) => !existingNames.includes(file.name));

  //   setForm((prev) => ({
  //     ...prev,
  //     attachments: [...prev.attachments, ...uniqueFiles],
  //   }));
  // };

  const fetchImageFromUrl = async (url) => {
    try {
      // 1. Data URLs
      if (url.startsWith('data:')) {
        const arr = url.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        const fileExt = mime.split('/')[1] || 'png';
        return new File([u8arr], `image_${Date.now()}.${fileExt}`, { type: mime });
      }

      // 2. HTTP/HTTPS URLs
      const res = await fetch(url);
      if (!res.ok) return null;
      const blob = await res.blob();

      if (!blob.type || !blob.type.startsWith('image/')) {
        return null;
      }

      let filename = 'downloaded_image';
      try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;
        const parts = pathname.split('/');
        const lastPart = parts[parts.length - 1];
        if (lastPart && lastPart.includes('.')) {
          filename = lastPart;
        } else {
          const ext = blob.type.split('/')[1] || 'png';
          filename = `image_${Date.now()}.${ext}`;
        }
      } catch {
        const ext = blob.type.split('/')[1] || 'png';
        filename = `image_${Date.now()}.${ext}`;
      }

      return new File([blob], filename, { type: blob.type });
    } catch (err) {
      console.error('Error fetching image from URL:', err);
      return null;
    }
  };

  const addFiles = (filesToAdd) => {
    const existingNames = form.attachments.map((file) => file.name);
    let errorMsg = '';
    const validFiles = [];
    const allowedExtensions = ['png', 'jpg', 'jpeg', 'pdf', 'docx', 'xls', 'xlsx', 'csv', 'txt', 'zip'];

    for (let i = 0; i < filesToAdd.length; i++) {
      const file = filesToAdd[i];
      if (existingNames.includes(file.name)) continue;

      const extension = file.name.split('.').pop().toLowerCase();

      if (!allowedExtensions.includes(extension)) {
        errorMsg = `Invalid file format: ${file.name}. Allowed formats: png, jpg, jpeg, pdf, docx, xls, xlsx, csv, txt, zip.`;
        break;
      }

      if (extension === 'zip') {
        if (file.size > 3 * 1024 * 1024) {
          errorMsg = `Zip file ${file.name} exceeds 3MB limit.`;
          break;
        }
      } else {
        if (file.size > 1.5 * 1024 * 1024) {
          errorMsg = `File ${file.name} exceeds 1.5MB limit. Please convert it to a zip file and upload it.`;
          break;
        }
      }

      validFiles.push(file);
    }

    if (errorMsg) {
      setErrors((prev) => ({
        ...prev,
        attachments: errorMsg
      }));
      return;
    }

    if (validFiles.length > 0) {
      setForm((prev) => ({
        ...prev,
        attachments: [...prev.attachments, ...validFiles],
      }));
      setErrors((prev) => ({
        ...prev,
        attachments: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    addFiles(newFiles);
    e.target.value = null;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = [];

    // 1. Try to get files from items (which includes dragged images from websites in Chromium)
    if (e.dataTransfer.items) {
      for (let i = 0; i < e.dataTransfer.items.length; i++) {
        const item = e.dataTransfer.items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }
    } else if (e.dataTransfer.files) {
      // Fallback
      for (let i = 0; i < e.dataTransfer.files.length; i++) {
        files.push(e.dataTransfer.files[i]);
      }
    }

    // 2. If no files were found (or it was dragged as link/url), try fetching URL
    if (files.length === 0) {
      const uriList = e.dataTransfer.getData('text/uri-list');
      const plainText = e.dataTransfer.getData('text/plain');
      let targetUrl = '';

      if (uriList) {
        targetUrl = uriList.split('\n')[0].trim();
      } else if (plainText && plainText.startsWith('http')) {
        targetUrl = plainText.trim();
      }

      if (targetUrl) {
        const fetchedFile = await fetchImageFromUrl(targetUrl);
        if (fetchedFile) {
          files.push(fetchedFile);
        } else {
          if (targetUrl.startsWith('blob:')) {
            setErrors((prev) => ({
              ...prev,
              attachments: "Could not download image from cross-origin blob URL. Please copy (Ctrl+C) the image from WhatsApp and paste (Ctrl+V) it onto this upload area, or download and upload it."
            }));
          } else {
            setErrors((prev) => ({
              ...prev,
              attachments: "Failed to download image from the dropped URL. Try copy-pasting the image (Ctrl+C then Ctrl+V) or downloading it first."
            }));
          }
        }
      }
    }

    if (files.length > 0) {
      addFiles(files);
    }
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const files = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const file = items[i].getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }

    if (files.length > 0) {
      e.preventDefault();
      addFiles(files);
    }
  };



  const removeAttachment = (indexToRemove) => {
    setForm((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, index) => index !== indexToRemove),
    }));
  };

  const validate = () => {
    const newErrors = {};

    // Admin-specific fields
    if (isAdmin && !form.client && !isEditMode) {
      newErrors.client = 'Client is required';
    }
    if (isAdmin && !form.customer && !isEditMode) {
      newErrors.customer = 'Customer is required';
    }
    // Common required fields
    const effectiveModule = form.module || module_name || ticket?.module_name || ticket?.module_id;
    if (!effectiveModule) {
      newErrors.module = 'Module is required';
    }
    if (!form.priority) {
      newErrors.priority = 'Priority is required';
    }
    if (!form.status) {
      newErrors.status = 'Status is required';
    }
    if (!form.title.trim() && !isEditMode) {
      newErrors.title = 'Title is required';
    }

    // For internal tickets (ENOVA) or Edit Mode, Assigned To is required
    const isInternal = String(form.client) === '108';
    if (userInfo?.role_id != '2' && showAssignee && (!form.assigned_to || form.assigned_to.length === 0) && (isEditMode || isInternal)) {
      newErrors.assigned_to = 'Assigned to is required';
    }

    // if (!form.issue_type && isEditMode && (userInfo.role_id == '1' || userInfo.role_id == '3')) {
    //   newErrors.issue_type = 'Issue Type is required';
    // }
    const isIssueTypeEditable = userInfo?.role_id === '1' || userInfo?.role_id === '3' || [25, 44].includes(Number(userInfo?.designation_id));
    if (!form.issue_type && userInfo?.role_id != '2' && isIssueTypeEditable) {
      newErrors.issue_type = 'Issue Type is required';
    }

    if (form.issue_type === 'Enhancement' && !form.enhancement_type) {
      newErrors.enhancement_type = 'Enhancement Type is required';
    }

    if (form.issue_type === 'Enhancement' && form.enhancement_type === 'Billable' && !form.enhancement_hours) {
      newErrors.enhancement_hours = 'Enhancement Hours are required';
    }

    // Clean and validate rich-text description
    const plainDescription = form.description.replace(/<(.|\n)*?>/g, '').trim();
    if (!plainDescription) {
      newErrors.description = 'Description is required';
    }

    // Recovery & Restart (2 fields) — mandatory ONLY for developers moving a Bug to Testing
    const rrRoleId = Number(userInfo?.role_id);
    const rrUserId = Number(userInfo?.user_id);
    const rrIsTester = rrRoleId === 5 || Number(userInfo?.designation_id) === 42;
    const isDeveloper = ![1, 2, 3].includes(rrRoleId) && rrUserId !== 101 && !rrIsTester;
    const isTestingBug = isEditMode && String(form.status) === '8' && form.issue_type === 'Bug' && isDeveloper;
    if (isTestingBug) {
      if (!form.recovery_steps.trim()) {
        newErrors.recovery_steps = 'Problem Recovery is required.';
      }
      if (!form.restart_steps.trim()) {
        newErrors.restart_steps = 'Restart Procedure is required.';
      }
    }

    setErrors(newErrors);
    console.log(newErrors);
    return Object.keys(newErrors).length === 0;
  };



  const handleSubmit = async (e) => {
    e?.preventDefault(); // Allow calling manually without event

    // console.log("VALIDATE START");
    const isValid = validate();
    // console.log("VALIDATE RESULT", isValid);
    if (!isValid) return;

    // console.log("AFTER VALIDATE, before formData ");
    setSubmitting(true);

    try {
      const formData = new FormData();

      // Append fields
      formData.append('ticketTitle', form.title);
      formData.append('priority', form.priority);
      formData.append('status', form.status);
      formData.append('description', form.description);
      formData.append('module', form.module || module_name || ticket?.module_name || ticket?.module_id || '');
      formData.append('recovery_steps', form.recovery_steps || '');
      formData.append('restart_steps', form.restart_steps || '');

      // Handle assigned_to - send as array for internal tickets, single value for others
      const isInternal = String(form.client) === '108';
      if (isInternal && Array.isArray(form.assigned_to)) {
        // For internal tickets, send as array
        form.assigned_to.forEach((assignee) => {
          formData.append('assigned_to[]', assignee);
        });
      } else {
        // For non-internal tickets or edit mode, send single value
        const assigneeValue = Array.isArray(form.assigned_to) ? form.assigned_to[0] : form.assigned_to;
        formData.append('assigned_to', assigneeValue || '');
      }

      // Handle CC
      if (isInternal && Array.isArray(form.internal_cc)) {
        form.internal_cc.forEach((ccId) => {
          formData.append('internal_cc[]', ccId);
        });
      }

      // Identity
      // formData.append('user_id', userInfo.user_id);
      // formData.append('role_id', userInfo.role_id);
      formData.append('designation_id', userInfo.designation_id);

      if (isAdmin) {
        formData.append('cust_key', form.client);
        formData.append('child_id', form.customer);
      } else {
        formData.append('cust_key', userInfo.cust_key);
        formData.append('child_id', userInfo.child_id);
      }

      const clientIdToSend =
        userInfo.role_id === '2' ? userInfo.cust_key : form.client;

      if (isAdmin) {
        formData.append('clientId', clientIdToSend);
        formData.append('customerId', userInfo.name);
      } else {
        formData.append('clientId', userInfo?.cust_key);
        formData.append('clientName', form.customer);
      }
      formData.append('issue_type', form.issue_type);

      formData.append('issue_type', form.issue_type);
      if (form.issue_type === 'Enhancement') {
        formData.append('enhancement_type', form.enhancement_type);
        if (form.enhancement_type === 'Billable') {
          formData.append('enhancement_hours', form.enhancement_hours);
        }
      }
      formData.append('customer_name', ticket?.customer_name);

      // for (let [key, value] of formData.entries()) {
      //   console.log(`${key}:`, value);
      // }
      // return;

      for (let i = 0; i < form.attachments.length; i++) {
        formData.append('files[]', form.attachments[i]);
      }

      // Determine create or update
      const endpoint = selectedTicketId ? `/ticket/${selectedTicketId}` : '/ticket';
      const method = selectedTicketId ? 'post' : 'post';

      const res = await api({
        method,
        url: endpoint,
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.status) {
        try {
          onSuccess?.();
        } catch (callbackErr) {
          console.error('onSuccess callback error:', callbackErr);
        }
        onClose?.();
      } else {
        alert(res.data.message || 'Ticket submission failed');
      }

    } catch (err) {
      console.error('Ticket submission failed:', err);
      alert('An error occurred while submitting the ticket.');
    } finally {
      setSubmitting(false);
    }
  };


  const renderError = (field) =>
    errors[field] ? <p className="text-xs text-red-500 mt-1">{errors[field]}</p> : null;

  const restrictedStatusMap = {
    'New': ['6'],
    'IN process': ['1', '6'],
    'Pending verification & closure': ['1'],
    'Closed': ['1', '3', '4'],
    'Reopen': ['1']
  };

  const filteredClients = clientList?.filter(client => {
    const roleId = String(userInfo?.role_id);
    const designationId = Number(userInfo?.designation_id);

    // Admin (role_id 1), CA (role_id 3), and Project Manager (44) see all clients
    // if (roleId === '1' || roleId === '3' || designationId === 44) {

    if (roleId === '1' || roleId === '3' || designationId === 44 || designationId === 63) {
      return true;
    }
    // All other employees see only ENOVA (cust_key 108)
    return String(client.cust_key) === '108';
  });

  useEffect(() => {
    if (filteredClients?.length === 1 && !form.client) {
      const defaultClient = filteredClients[0];

      // Update form state
      setForm(prev => ({
        ...prev,
        client: defaultClient.cust_key,
        customer: '',
        module: ''
      }));

      // Manually trigger customer fetch logic
      (async () => {
        setModuleOptions([]);
        setCustomerOptions([]);
        setIsCustomerLoading(true);
        try {
          const res = await api.get('/ticket/get-customers', {
            params: { cust_key: defaultClient.cust_key },
          });
          if (res.data.status) {
            setCustomerOptions(res.data.data);
          }
        } catch (error) {
          console.error('Failed to fetch customers:', error);
        } finally {
          setIsCustomerLoading(false);
        }
      })();
    }
  }, [filteredClients, form.client]);


  useEffect(() => {
    if (selectedClientId) {
      handleChange({
        target: { name: "client", value: selectedClientId },
      });
    }
  }, [selectedClientId]);



  return (
    <div className="bg-white max-w-6xl mx-auto mb-8">
      <form onSubmit={handleSubmit} className="space-y-2">
        <div className="grid grid-cols-2 md:grid-cols-2 gap-2">
          {isAdmin && (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1 ">Client <span className="text-red-500">*</span></label>
                <select
                  name="client"
                  value={form.client}
                  onChange={handleChange}
                  className="block w-full rounded border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">Select Client</option>

                  {filteredClients.map(client => (
                    <option key={client.cust_key} value={client.cust_key}>
                      {client.short_code?.trim() ? client.short_code : client.customer_name}
                    </option>
                  ))}
                </select>
                {renderError('client')}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 flex items-center ">
                  Customer Name <span className="text-red-500 ml-1">*</span>
                  {isCustomerLoading && (
                    <span className="animate-spin inline-block w-4 h-4 border-[2px] border-current border-r-transparent rounded-full align-middle ml-2 "></span>
                  )}
                </label>
                <select
                  name="customer"
                  value={form.customer}
                  onChange={handleChange}
                  className="block w-full rounded border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">Select Customer</option>
                  {customerOptions.map((cust) => (
                    <option key={cust.cust_key} value={cust.cust_key}>
                      {cust.customer_name}
                    </option>
                  ))}
                </select>

                {renderError('customer')}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1 flex items-center ">
              Module <span className="text-red-500 ml-1">*</span>
              {isModuleLoading && (
                <span className="animate-spin inline-block w-4 h-4 border-[2px] border-current border-r-transparent rounded-full align-middle ml-2 text-blue-500"></span>
              )}
            </label>
            <select
              name="module"
              value={form.module || module_name}
              onChange={handleChange}
              disabled={selectedTicketChildId !== '' && module_name !== ''}
              className={`block w-full rounded px-3 py-2 text-sm  appearance-none ${errors.module ? 'border-red-500' : 'border-gray-300'
                } border bg-white disabled:bg-gray-100 cursor-${selectedTicketChildId !== '' ? 'not-allowed' : 'default'}`}
            >
              <option value="">Select Module</option>
              {(module_name || form.module) && !moduleOptions.some(opt => opt.label === module_name || String(opt.value) === String(form.module)) && (
                <option value={form.module || module_name}>{module_name || form.module}</option>
              )}
              {moduleOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {renderError('module')}
          </div>

        </div>
        <div className="grid grid-cols-3 md:grid-cols-3 gap-2">

          <div>
            <label className="block text-sm font-medium mb-1 ">Priority <span className="text-red-500">*</span></label>
            <select
              name="priority"
              value={form.priority}
              onChange={handleChange}
              className={`block w-full rounded px-3 py-2 text-sm  appearance-none ${errors.priority ? 'border-red-500' : 'border-gray-300'
                } border`}
            >
              <option value="">Select Priority</option>
              {priorityOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {renderError('priority')}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 ">Status <span className="text-red-500">*</span></label>
            <select
              name="status"
              value={form.status}
              onChange={handleChange}
              className={`block w-full rounded px-3 py-2 text-sm  appearance-none ${errors.status ? 'border-red-500' : 'border-gray-300'
                } border`}
            >
              {selectedTicketId && <option value="">Select Status</option>}

              {(
                selectedTicketId
                  ? statusOptions.filter((opt) => {
                    const roleId = Number(userInfo?.role_id);
                    const userId = Number(userInfo?.user_id);
                    const currentStatusId = Number(selectedStatusId);
                    const optValue = Number(opt.value);
                    const designationId = Number(userInfo?.designation_id);

                    // Allow "On Hold" (13) for all employees (non-clients) at any stage
                    if (optValue === 13) {
                      return roleId !== 2;
                    }

                    // 0. Super User Access (Admin or Vertical Head)
                    const isInternal = String(ticket?.client_key) === '108';
                    const isCreator = String(ticket?.created_by) === String(userInfo?.user_id);

                    // System Admin (Role 1) gets full access to all statuses
                    if (roleId === 1) {
                      return true;
                    }

                    // Vertical Heads: 25, 26, 42, 43, 44, 63 get full management access EXCEPT closing tickets opened by others / external tickets
                    const isVerticalHead = [25, 26, 42, 43, 44, 63].includes(designationId);
                    if (isVerticalHead) {
                      if (optValue === 5 && !(isInternal && isCreator)) {
                        return false;
                      }
                      return true;
                    }

                    // 1. Workflow Logic for Testing Statuses
                    // ------------------------------------

                    const isTester = (roleId === 5 || Number(userInfo?.designation_id) === 42);

                    // 0. Internal Ticket Status Overrides
                    if (isInternal) {
                      // Creator gets full access
                      if (String(ticket?.created_by) === String(userInfo?.user_id)) {
                        return true;
                      }
                      // Assignee gets full access except for Closing and Reopening
                      if (String(ticket?.assign) === String(userInfo?.user_id)) {
                        return ![5, 6].includes(optValue); // 5: Closed, 6: Reopen
                      }
                    }

                    // Customer (Role 2) - Context-sensitive status options based on live screenshots
                    if (roleId === 2) {
                      // Status: IN process → Show: IN process, Closed
                      if (currentStatusId === 4) {
                        return [4, 5].includes(optValue);
                      }
                      // Status: Pending verification & closure → Show: IN process, Closed
                      if (currentStatusId === 3) {
                        return [4, 5].includes(optValue);
                      }
                      // Status: Closed → Show: Closed, Reopen
                      if (currentStatusId === 5) {
                        return [5, 6].includes(optValue);
                      }
                      // Status: Reopen → Show: IN process, Reopen
                      if (currentStatusId === 6) {
                        return [4, 6].includes(optValue);
                      }
                      // Status: Pending & Customer Confirmation
                      if (currentStatusId === 12) {
                        return [4, 5].includes(optValue); // IN process, Closed
                      }
                      // Any other status (New, etc.) — hide testing/special statuses AND Reopen
                      return ![6, 8, 9, 10, 12].includes(optValue);
                    }

                    // Tester Actions (Role 5 or Designation 42)
                    if (isTester) {
                      const isInternal = String(ticket?.client_key) === '108';
                      if ([8, 9, 10].includes(currentStatusId)) {
                        // In active testing phase — show only testing-related statuses
                        return [8, 9, 10, currentStatusId].includes(optValue);
                      }
                      // Outside testing phase
                      if (isInternal) {
                        // For internal: allow Pending verification (3), plus standard IN Process/Testing
                        return [1, 3, 4, 8].includes(optValue);
                      }
                      // For external: allow IN Process + Testing only
                      return [4, 8].includes(optValue);
                    }

                    // Developer/Designer (Role 4/Employee) - No testing actions, ONLY Test (8)
                    if (![1, 2, 3].includes(roleId) && userId !== 101 && !isTester) {
                      if (isInternal) {
                        // Internal tickets: only ticket creator or Admin/CA can Close (5) or Reopen (6)
                        if ([5, 6].includes(optValue)) {
                          return (
                            ['1', '3'].includes(String(userInfo?.role_id))
                          );
                        }
                        // All other statuses for internal (Creator also gets 5/6 from the Primary Check above)
                        return [1, 3, 4, 8, 12].includes(optValue);
                      }
                      // External tickets: Developer can do IN Process, Testing (cannot use Pending Customer Verification & Confirmation)
                      return [4, 8].includes(optValue);
                    }

                    // Mari Sir (Test Head) Actions - Sees all statuses
                    if (userId === 101) {
                      return true;
                    }

                    // Tester Actions
                    if (currentStatusId === 8 && userId === Number(ticket?.assign_user_id)) {
                      return [8, 9, 10].includes(optValue); // Approved (9), Rejected (10)
                    }

                    // CA Actions after Testing Approved
                    if ([1, 3].includes(roleId)) {
                      // ONLY FOR EXTERNAL TICKETS: Hide Closed (5) and Reopen (6) Options (Client handles this)
                      if (!isInternal && [5, 6].includes(optValue)) {
                        return false;
                      }

                      if (currentStatusId === 9) {
                        return [9, 3].includes(optValue); // Approved moves to Pending (3) for client
                      }
                      // CA can forward Pending & Customer Confirmation to client (no assignee needed)
                      // OR send back to developer as IN Process
                      if (currentStatusId === 12) {
                        return [4, 12].includes(optValue); // IN Process (back to dev) or Pending (fwd to client)
                      }
                    }


                    // 2. Existing Legacy Restrictions
                    // ------------------------------------
                    if (isInternal) {
                      // Restriction fallback for other users who aren't Creator/Assignee
                      if (optValue === 5) {
                        return [1, 3].includes(roleId); // Only Admin/CA can close if not creator
                      }
                      // Exclude testing statuses from general internal view if not in testing phase
                      if ([8, 9, 10, 11].includes(optValue) && ![8, 9, 10, 11].includes(currentStatusId)) {
                        return false;
                      }
                      return true;
                    }

                    if (!isEditMode && opt.label === 'New') return false;
                    if (
                      String(opt.value) === '3' &&
                      !(
                        (userInfo?.role_id === '1' || userInfo?.role_id === '3') ||
                        (userInfo?.role_id === '2' && selectedStatusId === '3')
                      )
                    ) {
                      return false;
                    }
                    if (userInfo?.role_id != '2' && selectedStatusId === '3' && String(opt.value) === '4') return false;
                    if (userInfo?.role_id == '2' && selectedStatusId === '6' && String(opt.value) === '4') return false;
                    if (userInfo?.role_id != '1' && userInfo?.role_id != '3' && userInfo?.role_id != '2' && String(opt.value) === '5') return false;
                    const currentStatusLabel = ticket?.status;
                    const restricted = restrictedStatusMap[currentStatusLabel] || [];
                    return !restricted.includes(String(opt.value));
                  })
                  : [{ value: '1', label: 'New' }]
              ).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>


            {renderError('status')}
          </div>

          {((isEditMode && showAssignee) || String(form.client) === '108') && userInfo?.role_id != '2' && form.status != 3 && (
            <div>
              <label className="block text-sm font-medium mb-1 ">
                Assigned To <span className="text-red-500">*</span>
                {String(form.client) === '108' && !isEditMode && (
                  <span className="text-xs text-gray-500 ml-2">(Multi-select enabled)</span>
                )}
              </label>
              <Select
                ref={assigneeRef}
                name="assigned_to"
                options={assigneeOptions}
                isMulti={String(form.client) === '108' && !isEditMode} // Multi-select only for internal tickets in create mode
                value={
                  String(form.client) === '108' && !isEditMode
                    ? assigneeOptions.filter((opt) => (form.assigned_to || []).map(String).includes(String(opt.value)))
                    : assigneeOptions.find((opt) => String(opt.value) === String(Array.isArray(form.assigned_to) ? form.assigned_to[0] : form.assigned_to)) || null
                }
                placeholder="Select Assignee"
                onChange={(selected) => {
                  if (String(form.client) === '108' && !isEditMode) {
                    // Multi-select for internal tickets
                    setForm({ ...form, assigned_to: selected ? selected.map(s => s.value) : [] });
                  } else {
                    // Single select for others
                    setForm({ ...form, assigned_to: selected ? [selected.value] : [] });
                  }
                }}
                isClearable
                className="text-sm"
                classNamePrefix="react-select"
              />
              {renderError('assigned_to')}
            </div>
          )}

          {(String(form.client) === '108' || String(form.client).toUpperCase() === 'INTERNAL' || (isEditMode && (ticket?.client_name === '108' || String(ticket?.client_name).toUpperCase() === 'INTERNAL' || String(ticket?.client_name).toUpperCase().includes('ENOVA')))) && (
            <div className=''>
              <label className="block text-sm font-medium mb-1 ">
                CC (optional)
              </label>
              {isEditMode && (!form.internal_cc || form.internal_cc.length === 0) && (userInfo?.role_id !== '1' && userInfo?.role_id !== '3') ? (
                <div className="text-sm text-gray-500 bg-gray-50 border rounded px-3 py-2 italic">None</div>
              ) : (
                <Select
                  name="internal_cc"
                  options={verticalHeadsOptions}
                  isMulti
                  value={verticalHeadsOptions.filter(opt => (form.internal_cc || []).map(String).includes(String(opt.value)))}
                  placeholder="Select users to notify"
                  onChange={(selected) => setForm({ ...form, internal_cc: selected ? selected.map(s => s.value) : [] })}
                  isClearable
                  isDisabled={isEditMode && (userInfo?.role_id === '2' || (ticket?.client_name !== 'INTERNAL' && !['1', '3'].includes(userInfo?.role_id)))}
                  className="text-sm"
                  classNamePrefix="react-select"
                  menuPlacement="auto"
                />
              )}
            </div>
          )}

          {
            (
              isEditMode
                ? (userInfo?.role_id != '2' || form.issue_type !== '')
                : (userInfo?.role_id != '2')
            ) && (
              <div className=''>
                <label className="block text-sm font-medium mb-1 ">
                  Issue Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="issue_type"
                  value={form.issue_type}
                  onChange={handleChange}
                  disabled={
                    userInfo?.role_id === '2' ||
                    (isEditMode && ticket?.client_name !== 'INTERNAL' && !['1', '3'].includes(userInfo?.role_id) && ![25, 44].includes(Number(userInfo?.designation_id)))
                  }
                  className={`block w-full rounded px-3 py-2 text-sm   appearance-none ${errors.issue_type ? 'border-red-500' : 'border-gray-300'
                    } border bg-white ${userInfo?.role_id === '2' ||
                      (isEditMode && ticket?.client_name !== 'INTERNAL' && !['1', '3'].includes(userInfo?.role_id) && ![25, 44].includes(Number(userInfo?.designation_id)))
                      ? 'bg-gray-100 cursor-not-allowed'
                      : ''
                    }`}

                >
                  <option value="">Select Issue Type</option>
                  <option value="Bug">Bug</option>
                  <option value="Enhancement">Enhancement</option>

                  <option value="Others">Others</option>
                </select>
                {renderError('issue_type')}
              </div>
            )
          }

          {form.issue_type === 'Enhancement' && (
            <div>
              <label className="block text-sm font-medium mb-1 ">
                Enhancement Type <span className="text-red-500">*</span>
              </label>
              <select
                name="enhancement_type"
                value={form.enhancement_type}
                onChange={handleChange}
                disabled={
                  userInfo?.role_id === '2' ||
                  (isEditMode && ticket?.client_name !== 'INTERNAL' && !['1', '3'].includes(userInfo?.role_id))
                }
                className={`block w-full rounded px-3 py-2 text-sm   appearance-none ${errors.issue_type ? 'border-red-500' : 'border-gray-300'
                  } border bg-white ${userInfo?.role_id === '2' ||
                    (isEditMode && ticket?.client_name !== 'INTERNAL' && !['1', '3'].includes(userInfo?.role_id))
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                  }`}
              >
                <option value="">Select Enhancement Type</option>
                <option value="Billable">Billable</option>
                <option value="Non-Billable">Non-Billable</option>
              </select>
              {renderError('enhancement_type')}
            </div>
          )}

          {/* || form.enhancement_type === 'Non-Billable' */}
          {(form.enhancement_type === 'Billable') &&
            (
              !isEditMode || // show always in create mode
              userInfo?.role_id === '2' ||
              ticket?.client_name === 'INTERNAL' ||
              (ticket?.client_name !== 'INTERNAL' && ['1', '3'].includes(userInfo?.role_id))
            ) && (
              <div>
                <label className="block text-sm font-medium mb-1 ">
                  Enhancement Hours <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="enhancement_hours"
                  value={form.enhancement_hours}
                  onChange={handleChange} // Always allow typing
                  onBlur={(e) => {
                    const value = e.target.value.trim();

                    // allow numbers below 1000, up to 2 decimals
                    const isValidFormat = /^(?:0|[1-9]\d{0,2})(?:\.\d{1,2})?$/.test(value);

                    if (!value) {
                      setErrors((prev) => ({ ...prev, enhancement_hours: null }));
                      return;
                    }

                    if (!isValidFormat) {
                      setErrors((prev) => ({ ...prev, enhancement_hours: 'Max allowed: below 730 hours' }));
                      return;
                    }

                    const number = Number(value);

                    if (number >= 730) {
                      setErrors((prev) => ({ ...prev, enhancement_hours: 'Max allowed: below 730 hours' }));
                    } else {
                      setErrors((prev) => ({ ...prev, enhancement_hours: null }));
                    }
                  }}
                  placeholder="Enter hours (Max 730 hours)"
                  className={`block w-full rounded px-3 py-2 text-sm appearance-none ${errors.enhancement_hours ? 'border-red-500' : 'border-gray-300'
                    } border bg-white ${userInfo?.role_id === '2' ||
                      (isEditMode && ticket?.client_name !== 'INTERNAL' && !['1', '3'].includes(userInfo?.role_id))
                      ? 'bg-gray-100 cursor-not-allowed'
                      : ''
                    }`}
                />

                {renderError('enhancement_hours')}
              </div>
            )
          }



        </div>


        {!selectedTicketId &&
          <div>
            <label className="block text-sm font-medium mb-1 ">
              Ticket Title <span className="text-red-500">*</span>
            </label>

            <input
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              maxLength={150}
              placeholder="e.g. Unable to upload file"
              className={`block w-full rounded  text-sm   appearance-none ${errors.title ? 'border-red-500' : 'border-gray-300'
                } border`}
            />

            <div className="text-xs text-gray-500 mt-1 text-right">
              {form.title.length}/150 characters
            </div>

            {renderError('title')}
          </div>

        }

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <ReactQuill
              ref={descriptionRef}
              theme="snow"
              value={form.description}
              onChange={(val) => setForm((prev) => ({ ...prev, description: val }))}
              placeholder="Briefly describe the issue or request..."
              modules={{ toolbar: minimalToolbar }}
              className="custom-quill rounded-md border border-gray-300 bg-white"
            />
            {renderError('description')}
            {/* onKeyDown={handleKeyDown} */}

          </div>

          <div>
            <label className="block text-sm font-medium mb-1 ">Attachments</label>

            {/* Upload Zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onPaste={handlePaste}
              className={`relative border border-dashed rounded p-1.5 text-center transition-all ${isDragging ? 'border-blue-500 bg-blue-50/50 scale-[1.01]' : 'border-gray-300'
                }`}
            >
              <input
                type="file"
                multiple
                accept=".png,.jpg,.jpeg,.pdf,.docx,.xls,.xlsx,.csv,.txt,.zip"
                onChange={handleFileChange}
                className="absolute inset-0 opacity-0 cursor-pointer"
                id="fileUpload"
              />
              <div className="flex items-center justify-center">
                <CloudArrowUpIcon className="w-6 h-6 text-gray-700" />

              </div>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-700 pointer-events-none">
                <PaperClipIcon className="h-4 w-4 text-gray-500" />
                <span className="font-medium text-md">Drag or click to upload</span>
              </div>
              <div className="text-[10px] mt-2 leading-tight text-center text-gray-500 pointer-events-none space-y-1">
                <p>
                  <span className="font-bold text-gray-700">Accepted formats:</span> png, jpg, jpeg, pdf, docx, xls, xlsx, csv, txt, zip.
                </p>
                <p>
                  <span className="font-bold text-gray-700">Maximum size:</span> 1.5 MB per file. Zip files up to 3 MB are allowed.
                </p>
                <p className="italic text-red-500">
                  If your file exceeds 1.5 MB, please convert it into a zip file and upload it.
                </p>
              </div>
            </div>

            {/* 📎 List of Attachments — placed outside to avoid triggering input */}
            {renderError('attachments')}
            {form.attachments.length > 0 && (
              <ul className="mt-2 space-y-1 text-xs text-left">
                {form.attachments.map((file, index) => (
                  <li
                    key={index}
                    className="flex items-center justify-between gap-2 bg-white px-2 py-1 rounded border border-gray-200"
                  >
                    <span className="truncate flex-1">📎 {file.name}</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault(); // Optional safety
                        e.stopPropagation();
                        removeAttachment(index);
                      }}
                      className="text-red-500 hover:text-red-700 text-xs font-bold"
                      title="Remove"
                    >
                      ❌
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Recovery & Restart – DEVELOPER only: Bug → Testing, full-width below Description+Attachments */}
        {(() => {
          const rrRoleId = Number(userInfo?.role_id);
          const rrUserId = Number(userInfo?.user_id);
          const rrIsTester = rrRoleId === 5 || Number(userInfo?.designation_id) === 42;
          const isDeveloper = ![1, 2, 3].includes(rrRoleId) && rrUserId !== 101 && !rrIsTester;
          if (isEditMode && String(form.status) === '8' && form.issue_type === 'Bug' && isDeveloper) {
            return (
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                {/* Section header */}
                <p className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <span>🔄</span> Recovery &amp; Restart
                  <span className="text-red-500">*</span>
                  <span className="text-xs font-normal text-gray-400">(Required for Bug → Testing)</span>
                </p>

                {/* Two-column grid — equal-width columns */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                  {/* Column 1 – Problem Recovery */}
                  <div className="flex flex-col">
                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1 flex items-center gap-1">
                      <span>🛠</span> Problem Recovery
                      <span className="text-red-500 ml-0.5">*</span>
                    </label>
                    <p className="text-[11px] text-gray-400 mb-1 leading-snug">
                      Explain how the issue / bug was identified and resolved.
                    </p>
                    <textarea
                      name="recovery_steps"
                      value={form.recovery_steps}
                      onChange={handleChange}
                      rows={5}
                      placeholder="Describe how the issue was identified, what caused it, and how it was resolved..."
                      className={`block w-full rounded-md px-3 py-2 text-sm border resize-y focus:outline-none focus:ring-2 focus:ring-gray-300 ${errors.recovery_steps ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
                        }`}
                    />
                    {renderError('recovery_steps')}
                  </div>

                  {/* Column 2 – Restart Procedure */}
                  <div className="flex flex-col">
                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1 flex items-center gap-1">
                      <span>🔁</span> Restart Procedure
                      <span className="text-red-500 ml-0.5">*</span>
                    </label>
                    <p className="text-[11px] text-gray-400 mb-1 leading-snug">
                      List the steps followed to restart the system along with that client impact.
                    </p>
                    <textarea
                      name="restart_steps"
                      value={form.restart_steps}
                      onChange={handleChange}
                      rows={5}
                      placeholder="Describe the steps taken to restart the system / service after applying the fix, along with that client impact..."
                      className={`block w-full rounded-md px-3 py-2 text-sm border resize-y focus:outline-none focus:ring-2 focus:ring-gray-300 ${errors.restart_steps ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
                        }`}
                    />
                    {renderError('restart_steps')}
                  </div>

                </div>
              </div>
            );
          }
          return null;
        })()}


        <div className="flex flex-wrap items-center justify-end gap-3 mt-4">
          {(onNext || onPrevious) && (
            <div className="flex items-center bg-[#f1f5f7] rounded-xl px-2 py-1 border border-slate-200/80 shadow-xs">
              <button
                type="button"
                onClick={onPrevious}
                disabled={!hasPrevious}
                className="px-3 py-1 rounded-lg hover:bg-white hover:shadow-xs disabled:opacity-30 disabled:hover:bg-transparent transition-all flex items-center gap-1 text-sm font-bold text-slate-600 hover:text-slate-900"
                title="Previous Ticket"
              >
                <FiChevronLeft size={16} /> Prev
              </button>
              <div className="w-[1px] bg-slate-300 mx-1.5 h-4 my-auto" />
              <button
                type="button"
                onClick={onNext}
                disabled={!hasNext}
                className="px-3 py-1 rounded-lg hover:bg-white hover:shadow-xs disabled:opacity-30 disabled:hover:bg-transparent transition-all flex items-center gap-1 text-sm font-bold text-slate-800 hover:text-slate-950"
                title="Next Ticket"
              >
                Next <FiChevronRight size={16} />
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className={`min-w-[150px] inline-flex items-center justify-center px-6 py-2.5 font-bold rounded-xl text-sm text-white shadow-md transition-all active:scale-95 ${submitting ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"
              }`}
          >
            {submitting ? "Submitting..." : isEditMode ? "Submit Response" : "Submit Ticket"}
          </button>
        </div>
      </form>
    </div>
  );
}
